// AUDITUI.A3 (#2588) — the provenance line in the artefact header.
//
// Answers "who touched this last" before anyone clicks, and opens the history
// drawer. It owns the fetch; AuditTrail renders nothing it is not given.
//
// ── The shim, not the barrel ───────────────────────────────────────────────
// Audit symbols come from './auditShared', the relative-path shim, NOT from
// '@blueprint/shared'. Two independent reasons, and both outlive #2808 merging:
//   1. #2684 — in any worktree the specifier resolves to the PRIMARY checkout's
//      committed packages/shared, so barrel exports are invisible from a feature
//      worktree regardless of what has merged.
//   2. Module duplication — importing the same module by two paths yields two
//      instances, and AUDIT_RENDERER_REGISTRY is module-level singleton state.
// Adding a second import path here is what makes that hazard live.
//
// ── Why the actor is derived here and not taken from the sentence ──────────
// buildFallbackSentence's actor is `displayName ?? id` (audit-trail.ts:182-185),
// so it returns a RAW UUID whenever displayName is null — which is every row
// today, because toAuditActor never joins users (auditEventMapper.ts:134-142).
// Using sentence.actor would put `a3f4c8d2-…` in the header as if it were a
// person's name. Per the ruling on #2588 this line names an actor only when a
// name exists, never renders a UUID as a person, and never invents a
// placeholder. The verb still comes from the shared builder.
//
// ── No count ───────────────────────────────────────────────────────────────
// The response contract is frozen with no total (audit-trail.ts:17 — "No
// `total`. Cursor pagination is the surviving convention."), and a page-scoped
// count would be false the moment an artefact exceeds the page limit. The line
// ships without one. Restoring it is a contract amendment, not a UI change.

import { useCallback, useEffect, useRef, useState } from 'react';
import { buildAuditSentence } from './auditShared';
import type { AuditEvent, AuditPage } from './auditShared';
import ArtefactHistoryDrawer from './ArtefactHistoryDrawer';
import { apiFetch, ApiError } from '../../lib/api';
import { NO_ACCESSIBLE_ACTIVITY } from '@blueprint/shared';
import { formatTimeAgo } from '../../lib/dashboardFormat';

/**
 * The entity type an artefact's audit rows actually carry, confirmed from the
 * WRITE side rather than from fixtures: 37 createAuditLog call sites across 15
 * files pass exactly this literal (recon, #2588).
 *
 * `artefact_version` is a SEPARATE entity whose rows carry the version's own id
 * (sessions.ts:1929), so it is not reachable by widening this string — that is
 * an endpoint change, tracked as #2854.
 */
const ARTEFACT_ENTITY_TYPE = 'artefact';

/**
 * The minimum shape this component and AuditTrail actually dereference:
 * `action` (humanised into the verb, and `.toUpperCase()`d by the registry
 * lookup), `actor.kind`, `timestamp`, and `redactedFields`. Anything failing
 * this is dropped rather than rendered — a row that cannot be described is not
 * information, and crashing the artefact page over it is strictly worse than
 * omitting it.
 *
 * `redactedFields` is checked with Array.isArray, NOT for mere presence, and
 * the difference is the whole finding (#2859, reported by @gcloudvinodp-wq):
 *   - missing        -> AuditEventRow's `event.redactedFields.length > 0` throws
 *                       "Cannot read properties of undefined (reading 'length')"
 *   - a STRING       -> has .length, so it PASSES that check, reaches
 *                       RedactionMarker and throws "fields.join is not a function"
 * A guard written as `redactedFields !== undefined` fixes the first and leaves
 * the second live, and that is the version that looks sufficient.
 *
 * Both fire on PAGE LOAD, not on open: SlideOver stays mounted while closed, so
 * the rows render before anyone clicks the provenance line.
 *
 * Seven fields were probed on the render path. The other five are already read
 * defensively and need no guard — `reason` via truthiness, `entityType` via a
 * ternary, `actor.displayName` and `actor.id` via `??`, and `id` is a React key
 * only. See Sprint-Artefacts/2588-test-plan.md for the probe and its results.
 *
 * Deliberately a shape check, not a Zod schema: there is no runtime validation
 * anywhere at this client boundary today, and introducing a validation library
 * for one component would be a repo-wide decision this story does not own.
 */
function isRenderableEvent(e: unknown): e is AuditEvent {
  if (!e || typeof e !== 'object') return false;
  const ev = e as Partial<AuditEvent>;
  return (
    typeof ev.action === 'string' &&
    typeof ev.timestamp === 'string' &&
    !!ev.actor &&
    typeof ev.actor === 'object' &&
    typeof (ev.actor as { kind?: unknown }).kind === 'string' &&
    Array.isArray(ev.redactedFields)
  );
}

interface Props {
  artefactId: string;
  /** Injectable clock for deterministic relative times in tests. */
  now?: number;
}

/**
 * One settled fetch, TAGGED with the artefact it belongs to.
 *
 * The tag is what removes the reset. `loading` and `error` were separate state
 * that the effect had to clear synchronously on every `artefactId` change —
 * which is `react-hooks/set-state-in-effect`, and the rule is right: those
 * writes queue a second render pass on top of the one the prop change already
 * caused. Carrying the id inside the value instead lets both be DERIVED, so
 * there is nothing to reset and nothing to clear.
 */
type SettledTrail = {
  readonly artefactId: string;
  readonly events: readonly AuditEvent[];
  readonly error: string | null;
  /** #3872 — true when the server said no history is reachable (403 No accessible activity). */
  readonly unavailable?: boolean;
};

/** Stable identity — `events` is a prop, so a fresh `[]` per render would churn. */
const NO_EVENTS: readonly AuditEvent[] = [];

export default function ArtefactProvenanceLine({ artefactId, now }: Props) {
  const [settled, setSettled] = useState<SettledTrail | null>(null);
  const [open, setOpen] = useState(false);
  const openerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;

    apiFetch<AuditPage>(`/entities/${ARTEFACT_ENTITY_TYPE}/${artefactId}/audit`)
      .then((page) => {
        if (cancelled) return;
        // Defensive: a malformed response must not take the artefact page down.
        // A header widget degrades, it never crashes its host.
        //
        // apiFetch<AuditPage> is a COMPILE-TIME cast, not runtime validation —
        // nothing at this boundary checks the server's shape, so every read
        // below has to be earned.
        //
        // Two rounds of this, both recorded because the second is the lesson:
        //   1. `items` absent  -> setEvents(undefined) -> events[0] threw.
        //      Found by the full web suite, not by these tests.
        //   2. `items: [null]` / `[{}]` -> buildAuditSentence and actor.kind
        //      threw one level deeper. Found by R1, and reproduced before
        //      fixing: "Cannot read properties of null (reading 'id')" and
        //      "Cannot read properties of undefined (reading 'toUpperCase')".
        // Guarding the container alone was not enough; the items are guarded
        // too. Pinned by the malformed-shape cases in this component's tests.
        setSettled({
          artefactId,
          events: Array.isArray(page?.items) ? page.items.filter(isRenderableEvent) : [],
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // #3872 — the route answers 403 `No accessible activity` both when no audit row is recorded and
        // when none is visible to this caller (#2586: no existence oracle). Neither is a failure of the
        // page, so it is not shown as an error. The copy says only what is true in both cases (R1 P2 /
        // R2 P2): there is no history to show. Any other failure is still an error.
        if (err instanceof ApiError && err.status === 403 && err.message === NO_ACCESSIBLE_ACTIVITY) {
          setSettled({ artefactId, events: NO_EVENTS, error: null, unavailable: true });
          return;
        }
        setSettled({ artefactId, events: NO_EVENTS, error: 'Could not load history.' });
      });

    return () => {
      cancelled = true;
    };
  }, [artefactId]);

  const close = useCallback(() => setOpen(false), []);

  // Derived, never stored. A settled result belongs to exactly one artefact, so
  // a new id is UNLOADED by definition — which is the previous `setLoading(true)`
  // reset, minus the extra render pass and minus the frame in which the old
  // artefact's events were still on screen. This is the leak named in #2588's
  // adversarial focus, and it is now closed by construction rather than by a
  // clear that runs one render late.
  //
  // The `cancelled` flag still earns its place: it stops a late response from
  // writing at all. The id check is the second line — even a write that slipped
  // through cannot be read back under the wrong artefact.
  const current = settled && settled.artefactId === artefactId ? settled : null;
  const loading = current === null;
  const error = current?.error ?? null;
  const unavailable = current?.unavailable === true;
  const events = current?.events ?? NO_EVENTS;

  const latest = events[0];
  const sentence = latest ? buildAuditSentence(latest) : null;

  // A name, or nothing. Never an id, never a placeholder.
  const actorName =
    latest && latest.actor.kind === 'user' && latest.actor.displayName
      ? latest.actor.displayName
      : null;

  return (
    <>
      <button
        ref={openerRef}
        type="button"
        data-testid="artefact-provenance-line"
        onClick={() => setOpen(true)}
        style={{
          display:        'inline-flex',
          alignItems:     'center',
          gap:            6,
          background:     'none',
          border:         'none',
          padding:        0,
          cursor:         'pointer',
          fontFamily:     'var(--font-body)',
          fontSize:       12,
          color:          'var(--text-secondary)',
          textDecoration: 'underline',
          textUnderlineOffset: 2,
        }}
      >
        {loading && <span data-testid="artefact-provenance-loading">Loading history…</span>}

        {!loading && error && <span data-testid="artefact-provenance-error">{error}</span>}

        {!loading && !error && !latest && unavailable && (
          <span data-testid="artefact-provenance-unavailable">No history to show</span>
        )}

        {!loading && !error && !latest && !unavailable && (
          <span data-testid="artefact-provenance-none">No recorded activity</span>
        )}

        {!loading && !error && latest && sentence && (
          <span data-testid="artefact-provenance-summary">
            <span data-testid="artefact-provenance-verb">{sentence.verb}</span>{' '}
            <time dateTime={latest.timestamp} data-testid="artefact-provenance-time">
              {formatTimeAgo(latest.timestamp, now)}
            </time>
            {actorName && (
              <>
                {' by '}
                <span data-testid="artefact-provenance-actor">{actorName}</span>
              </>
            )}
          </span>
        )}
      </button>

      <ArtefactHistoryDrawer
        isOpen={open}
        onClose={close}
        events={events}
        loading={loading}
        error={error}
        emptyMessage={unavailable ? 'No history to show for this item.' : undefined}
        now={now}
      />
    </>
  );
}
